import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { http, formatApiError } from "@/lib/api";
import { FieldInput } from "@/components/admin/FieldInput";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function cleanItem(item, fields) {
  const out = { ...item };
  fields.filter((f) => f.type === "list").forEach((f) => { out[f.name] = (out[f.name] || []).map((s) => s.trim()).filter(Boolean); });
  return out;
}

export function SaveBar({ dirty, busy, onSave, onReset, testid }) {
  return (
    <div className="flex items-center gap-3">
      {dirty && <span className="text-xs text-[var(--gold)]">Unsaved changes</span>}
      {onReset && <button onClick={onReset} className="text-xs text-white/50 hover:text-white" data-testid={`${testid}-reset-btn`}>Restore defaults</button>}
      <button onClick={onSave} disabled={busy || !dirty} className="btn-gold !px-4 !py-1.5 !text-base disabled:opacity-40" data-testid={`${testid}-save-btn`}>Save</button>
    </div>
  );
}

export function useSectionSave(sectionKey) {
  const { reload } = useSite();
  const [busy, setBusy] = useState(false);
  const save = async (value) => {
    setBusy(true);
    try {
      await http.put(`/admin/content/${sectionKey}`, { value });
      await reload();
      toast.success("Saved. Changes are live.");
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    if (!window.confirm("Restore this section to the original defaults?")) return null;
    const { data } = await http.post(`/admin/content/${sectionKey}/reset`);
    await reload();
    toast.success("Defaults restored.");
    return data.value;
  };
  return { busy, save, reset };
}

export function ListEditor({ sectionKey, title, fields, titleField, blank, fixed = false }) {
  const { content } = useSite();
  const [items, setItems] = useState(content[sectionKey]);
  const [dirty, setDirty] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const { busy, save, reset } = useSectionSave(sectionKey);
  const t = `admin-${sectionKey}`;

  const update = (next) => { setItems(next); setDirty(true); };
  const setField = (i, name, v) => update(items.map((it, j) => (j === i ? { ...it, [name]: v } : it)));
  const move = (i, d) => { const n = [...items]; [n[i], n[i + d]] = [n[i + d], n[i]]; update(n); };
  const onSave = async () => {
    const value = items.map((it) => {
      const c = cleanItem(it, fields);
      if ("id" in c && !c.id) c.id = slugify(c.name || "item") || `item-${Date.now()}`;
      return c;
    });
    if (await save(value)) { setItems(value); setDirty(false); }
  };
  const onReset = async () => { const v = await reset(); if (v) { setItems(v); setDirty(false); } };

  return (
    <section className="rounded-md border border-white/10 bg-[#111] p-4 sm:p-6" data-testid={`${t}-editor`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="h-display text-3xl text-white">{title}</h2>
        <SaveBar dirty={dirty} busy={busy} onSave={onSave} onReset={onReset} testid={t} />
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="rounded-sm border border-white/10 bg-black/30" data-testid={`${t}-item-${i}`}>
            <div className="flex items-center gap-2 px-3 py-2">
              <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="flex flex-1 items-center gap-2 text-left text-sm font-semibold text-white" data-testid={`${t}-item-toggle-${i}`}>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openIdx === i ? "rotate-180" : ""}`} />
                <span className="truncate">{it[titleField] || "(untitled)"}</span>
              </button>
              {!fixed && (
                <>
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-white/50 hover:text-white disabled:opacity-20" aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
                  <button disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-white/50 hover:text-white disabled:opacity-20" aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
                  <button onClick={() => update(items.filter((_, j) => j !== i))} className="p-1 text-red-400/70 hover:text-red-400" aria-label="Delete" data-testid={`${t}-item-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
            {openIdx === i && (
              <div className="grid gap-4 border-t border-white/10 p-4 sm:grid-cols-2">
                {fields.map((f) => <FieldInput key={f.name} field={f} value={it[f.name]} onChange={(v) => setField(i, f.name, v)} testid={`${t}-${i}-${f.name}`} />)}
              </div>
            )}
          </div>
        ))}
      </div>
      {!fixed && (
        <button onClick={() => { update([...items, { ...blank }]); setOpenIdx(items.length); }} className="mt-4 flex items-center gap-2 text-sm text-[var(--gold)] hover:underline" data-testid={`${t}-add-btn`}>
          <Plus className="h-4 w-4" /> Add item
        </button>
      )}
    </section>
  );
}
